import React, { useState, useRef } from 'react';
import { useCvData } from '../context/CvDataContext.js';
import './GlobalBackup.css';

const GlobalBackup = ({ isEditing = false }) => {
  const { personalInfo, contactInfo, experience, education, patents, mainMedia } = useCvData();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleGlobalExport = async () => {
    if (!isEditing) {
      setMessage('Please enable edit mode to export data');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    setIsProcessing(true);
    
    try {
      // Create comprehensive backup object
      const backupData = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        siteName: 'Eli Elhadad CV Site',
        data: {
          personalInfo: personalInfo || {},
          contactInfo: contactInfo || {},
          experience: experience || [],
          education: education || [],
          patents: patents || [],
          mainMedia: mainMedia || []
        },
        metadata: {
          totalItems: {
            experience: (experience || []).length,
            education: (education || []).length,
            patents: (patents || []).length,
            media: (mainMedia || []).length
          },
          exportedBy: 'Admin Panel',
          exportedAt: new Date().toLocaleString()
        }
      };

      // Create and download the backup file
      const jsonString = JSON.stringify(backupData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `cv-site-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setMessage('Complete site backup downloaded successfully!');
      setMessageType('success');
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 5000);
      
    } catch (error) {
      setMessage(`Export failed: ${error.message}`);
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGlobalImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!isEditing) {
      setMessage('Please enable edit mode to import data');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check file type
    if (!file.name.endsWith('.json')) {
      setMessage('Please select a JSON backup file');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    setIsProcessing(true);

    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const backupData = JSON.parse(e.target.result);
          
          // Validate backup structure
          if (!backupData.data || !backupData.version) {
            throw new Error('Invalid backup file format');
          }

          // Show confirmation dialog
          const confirmMessage = `This will replace ALL current site data with the backup from ${backupData.metadata?.exportedAt || 'unknown date'}.\n\nBackup contains:\n- ${backupData.metadata?.totalItems?.experience || 0} experience items\n- ${backupData.metadata?.totalItems?.education || 0} education items\n- ${backupData.metadata?.totalItems?.patents || 0} patents\n- ${backupData.metadata?.totalItems?.media || 0} media items\n\nAre you sure you want to continue?`;
          
          if (!window.confirm(confirmMessage)) {
            setIsProcessing(false);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            return;
          }

          // Import each section
          const sections = ['personalInfo', 'contactInfo', 'experience', 'education', 'patents', 'mainMedia'];
          const results = [];

          for (const section of sections) {
            if (backupData.data[section]) {
              const apiUrl = process.env.NODE_ENV === 'development' 
                ? 'http://localhost:8888/.netlify/functions/update-cv'
                : '/.netlify/functions/update-cv';
              
              const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  section: section,
                  data: backupData.data[section]
                })
              });

              if (!response.ok) {
                throw new Error(`Failed to import ${section}: ${response.statusText}`);
              }

              const result = await response.json();
              results.push({ section, success: true, result });
            }
          }

          setMessage(`Successfully restored backup from ${backupData.metadata?.exportedAt || 'unknown date'}! All sections have been updated.`);
          setMessageType('success');
          
          // Clear message after 5 seconds
          setTimeout(() => {
            setMessage('');
            setMessageType('');
          }, 5000);

          // Reload the page to refresh all data
          setTimeout(() => {
            window.location.reload();
          }, 2000);

        } catch (error) {
          setMessage(`Import failed: ${error.message}`);
          setMessageType('error');
          setTimeout(() => {
            setMessage('');
            setMessageType('');
          }, 5000);
        } finally {
          setIsProcessing(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };
      
      reader.onerror = () => {
        setMessage('Failed to read backup file');
        setMessageType('error');
        setTimeout(() => {
          setMessage('');
          setMessageType('');
        }, 5000);
        setIsProcessing(false);
      };
      
      reader.readAsText(file);
      
    } catch (error) {
      setMessage(`Import failed: ${error.message}`);
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 5000);
      setIsProcessing(false);
    }
  };

  const handleImportClick = () => {
    if (!isEditing) {
      setMessage('Please enable edit mode to import data');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <div className="global-backup">
      <div className="backup-header">
        <h3>Global Backup & Restore</h3>
        <p>Export all site data or restore from a complete backup</p>
      </div>
      
      <div className="backup-controls">
        <button 
          onClick={handleGlobalExport}
          className={`backup-button export-button ${!isEditing ? 'disabled' : ''}`}
          title={isEditing ? 'Export complete site backup' : 'Enable edit mode to export backup'}
          disabled={!isEditing || isProcessing}
        >
          <i className="fas fa-download"></i>
          {isProcessing ? 'Exporting...' : 'Export Complete Backup'}
        </button>
        
        <button 
          onClick={handleImportClick}
          className={`backup-button import-button ${!isEditing ? 'disabled' : ''}`}
          title={isEditing ? 'Import complete site backup' : 'Enable edit mode to import backup'}
          disabled={!isEditing || isProcessing}
        >
          <i className="fas fa-upload"></i>
          {isProcessing ? 'Importing...' : 'Import Complete Backup'}
        </button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleGlobalImport}
          style={{ display: 'none' }}
        />
      </div>
      
      {message && (
        <div className={`backup-message ${messageType}`}>
          <i className={`fas ${messageType === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
          {message}
        </div>
      )}
      
      <div className="backup-info">
        <details>
          <summary>Global Backup Help</summary>
          <div className="backup-help-content">
            <h4>Export Complete Backup:</h4>
            <p>Downloads all site data (personal info, contact, experience, education, patents, media) as a single JSON file with metadata.</p>
            
            <h4>Import Complete Backup:</h4>
            <p>Uploads a complete backup file and replaces ALL current site data. This action cannot be undone!</p>
            
            <h4>Backup File Format:</h4>
            <p>The backup file includes version information, timestamps, and metadata about the exported data.</p>
            
            <h4>Safety:</h4>
            <p>Always export a backup before importing. The import process will ask for confirmation before replacing data.</p>
            
            <h4>Automatic Reload:</h4>
            <p>After successful import, the page will automatically reload to show the restored data.</p>
          </div>
        </details>
      </div>
    </div>
  );
};

export default GlobalBackup;
